import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { ButtonTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { EMAIL_TEMPLATES_URL } from "@constants/routes";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { FormattedEmailTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/email-template";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { navigateToUtil } from "@utils/navigation-util";
import { getEmailTemplateValidationErrors } from "@utils/validation/email-template/email-template-validation-util";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { EmailTemplateGeneralInfoTab } from "@containers/email-template-details/email-template-details-component/email-template-general-info-tab/email-template-general-info-tab";
import { EmailTemplateContentTab } from "@containers/email-template-details/email-template-details-component/email-template-content-tab/email-template-content-tab";
import styles from "./email-template-details-component.less";
import { IDWSession } from "@idw-types/data/idw-session";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import isEqual from "lodash/isEqual";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { ModalTypes } from "@enums/modal-types";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { useModalContext } from "@context/use-modal-context";
import { EmailTemplateDetailsTabTypes } from "@enums/email-template-details-tab-types";

type EmailTemplateDetailsComponentProps = {
    session: IDWSession;
    initialEmailTemplate: FormattedEmailTemplate;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    handleSave: (emailTemplateState: FormattedEmailTemplate) => Promise<FormattedEmailTemplate>;
};

export const EmailTemplateDetailsComponent = (props: EmailTemplateDetailsComponentProps) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { session, initialEmailTemplate, axnCompanies, selfServiceVerificationWhiteLabeledEmails, handleSave } = props;
    const [emailTemplateState, setEmailTemplateState] = useState<FormattedEmailTemplate>(initialEmailTemplate);
    const modalContext = useModalContext();

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [, setCurrentTab] = useState(EmailTemplateDetailsTabTypes.GENERAL_INFO);
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const resource = resources.pages.emailTemplateDetails;

    const [requestInProgress, setRequestInProgress] = useState(false);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(initialEmailTemplate, emailTemplateState);
    }, [initialEmailTemplate, emailTemplateState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-email-template-changes",
        modalType: ModalTypes.DiscardEmailTemplateChanges,
        stateIsUnchanged
    });

    useEffect(() => {
        setEmailTemplateState(initialEmailTemplate);
    }, [initialEmailTemplate])

    const validationErrors = useMemo(() => {
        return getEmailTemplateValidationErrors(emailTemplateState);
    }, [emailTemplateState]);

    const onCloseClick = () => {
        navigateToUtil(EMAIL_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const renderConfirmSaveEmailTemplateModal = (
        confirmMessage: string | React.ReactNode,
        onSubmit: (emailTemplateDetailsState: FormattedEmailTemplate) => Promise<void>
    ) => {
        const renderModal = (onModalClose: () => void) => {
            return (
                <ConfirmModal
                    id={"confirm-save-email-template"}
                    title={resources.modals.updateEmailTemplate.title}
                    confirmMessage={confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resources.common.buttons.save.label}
                    onSubmit={null}
                    onSubmitAsync={onSubmit}
                    onModalClose={onModalClose}
                    customProps={emailTemplateState}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    };

    const onSubmit = async () => {
        setRequestInProgress(true);
        await handleSave(emailTemplateState);
        setRequestInProgress(false);
    };

    const confirmMessage = useMemo(() => {
        return (
            <div>
                <p>{resources.modals.updateEmailTemplate.confirmMessageTitle}</p>
                <p>{resources.modals.updateEmailTemplate.confirmMessageDescription}</p>
            </div>
        );
    }, []);

    const onClickSave = async () => {
        renderConfirmSaveEmailTemplateModal(confirmMessage, onSubmit);
    };

    return (
        <div className={styles.emailTemplateDetailsComponent}>
            <div className={styles.headerContainer}>
                <span className={styles.templateName}>
                    {`${emailTemplateState.name}`}
                </span>
                <div className={styles.buttonContainer}>
                    <Button
                        id="email-template-details-close-button"
                        onClick={onCloseClick}
                        variant={"outline-secondary"}
                    >
                        {resources.common.buttons.close.label}
                    </Button>
                    {initialEmailTemplate.permissions.canModify && (
                        <ButtonWithSpinner
                            id="email-template-detail-save"
                            disabled={stateIsUnchanged || requestInProgress || validationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                            label={resources.common.buttons.save.label}
                            type={ButtonTypes.SUBMIT}
                            onClick={onClickSave}
                            variant={"primary"}
                        />
                    )}
                </div>
            </div>
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="email-template-details" fill={true} unmountOnExit={true} onSelect={setCurrentTab}>
                    <Tab eventKey={EmailTemplateDetailsTabTypes.GENERAL_INFO} title={validationErrors.generalInfo.tabTitle}>
                        <EmailTemplateGeneralInfoTab
                            isDisabled={!initialEmailTemplate.permissions.canModify}
                            session={session}
                            axnCompanies={axnCompanies}
                            selfServiceVerificationWhiteLabeledEmails={selfServiceVerificationWhiteLabeledEmails}
                            state={emailTemplateState}
                            labelWidth={labelWidth}
                            validationErrors={validationErrors.generalInfo}
                            setEmailTemplateState={setEmailTemplateState}
                        />
                    </Tab>
                    <Tab eventKey={EmailTemplateDetailsTabTypes.CONTENT} title={validationErrors.contentAndVariables.tabTitle}>
                        <div>
                            <ContentPageHeader title={resource.tabs.content.title} />
                            <ContentPageBody isMaxWidth={true}>
                                <EmailTemplateContentTab
                                    isDisabled={!initialEmailTemplate.permissions.canModify}
                                    state={emailTemplateState}
                                    labelWidth={labelWidth}
                                    selectedLanguage={selectedLanguage}
                                    contentValidationErrors={validationErrors.content}
                                    variablesValidationErrors={validationErrors.variables}
                                    setSelectedLanguage={setSelectedLanguage}
                                    setEmailTemplateState={setEmailTemplateState}
                                />
                            </ContentPageBody>
                        </div>
                    </Tab>
                </Tabs>
            </TabContainer>
        </div>
    );
};